import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { GripVertical, Pencil, Trash2, Plus } from "lucide-react";
import {
  DndContext, closestCenter, PointerSensor, useSensor, useSensors,
} from "@dnd-kit/core";
import {
  SortableContext, verticalListSortingStrategy, useSortable, arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { listWellsides, deleteWellside, reorderWellsides, updateWellside } from "@/admin/adminApi";

const fmt = (n) => (n == null ? "—" : `$${n.toLocaleString("en-NZ")}`);

const BadgeInput = ({ product, onSave }) => {
  const [value, setValue] = useState(product.badge || "");
  const [saving, setSaving] = useState(false);

  useEffect(() => setValue(product.badge || ""), [product.badge]);

  const commit = async () => {
    const trimmed = value.trim();
    if (trimmed === (product.badge || "")) return;
    setSaving(true);
    await onSave(product, trimmed || null);
    setSaving(false);
  };

  return (
    <input
      data-testid={`admin-wellside-badge-${product.slug}`}
      value={value}
      onChange={(e) => setValue(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()}
      disabled={saving}
      placeholder="Badge (e.g. Genuine OEM)"
      title="Label overlaid on the main product image — leave blank for none"
      className="w-32 shrink-0 bg-[var(--bg-main)] border border-[var(--border-default)] px-2 py-1.5 text-xs text-white focus:border-[var(--brand-primary)] outline-none placeholder:text-white/30"
    />
  );
};

const SortableRow = ({ product, onDelete, onToggleStock, onSaveBadge }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: product.id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 };

  return (
    <div
      ref={setNodeRef}
      style={style}
      data-testid={`admin-wellside-row-${product.slug}`}
      className="flex items-center gap-4 border-b border-[var(--border-default)] py-3 bg-[var(--bg-secondary)]"
    >
      <button {...attributes} {...listeners} data-testid={`admin-wellside-drag-${product.slug}`} className="cursor-grab text-white/40 hover:text-white shrink-0 px-2">
        <GripVertical size={18} />
      </button>
      <img src={product.thumb} alt={product.name} className="w-16 h-12 object-cover shrink-0 border border-[var(--border-default)]" />
      <div className="flex-1 min-w-0">
        <p className="text-white font-medium truncate">{product.name}</p>
      </div>
      <BadgeInput product={product} onSave={onSaveBadge} />
      <label
        className="flex items-center gap-2 shrink-0 cursor-pointer"
        data-testid={`admin-wellside-instock-toggle-${product.slug}`}
        title="Toggle whether Buy Now is available for this canopy"
      >
        <span className="text-[10px] font-mono uppercase tracking-widest text-white/60 w-[72px] text-right">
          {product.inStock !== false ? "In Stock" : "Buy Now Off"}
        </span>
        <button
          type="button"
          role="switch"
          aria-checked={product.inStock !== false}
          onClick={() => onToggleStock(product)}
          className={`relative w-9 h-5 rounded-full transition-colors shrink-0 ${
            product.inStock !== false ? "bg-[var(--brand-primary)]" : "bg-white/20"
          }`}
        >
          <span
            className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-black transition-transform ${
              product.inStock !== false ? "translate-x-4" : ""
            }`}
          />
        </button>
      </label>
      <div className="text-[var(--brand-primary)] font-mono text-sm w-24 shrink-0">
        {product.priceOnRequest ? "Enquire" : fmt(product.price)}
      </div>
      <Link
        to={`/admin/wellside-canopies/${product.slug}`}
        data-testid={`admin-wellside-edit-${product.slug}`}
        className="text-white/70 hover:text-[var(--brand-primary)] p-2"
      >
        <Pencil size={16} />
      </Link>
      <button
        onClick={() => onDelete(product)}
        data-testid={`admin-wellside-delete-${product.slug}`}
        className="text-white/70 hover:text-red-400 p-2"
      >
        <Trash2 size={16} />
      </button>
    </div>
  );
};

export default function AdminWellsideListPage() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  useEffect(() => {
    listWellsides().then((data) => {
      setProducts(data);
      setLoading(false);
    });
  }, []);

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = products.findIndex((p) => p.id === active.id);
    const newIndex = products.findIndex((p) => p.id === over.id);
    const reordered = arrayMove(products, oldIndex, newIndex);
    setProducts(reordered);
    await reorderWellsides(reordered.map((p) => p.id));
  };

  const handleDelete = async (product) => {
    if (!window.confirm(`Delete "${product.name}"? This cannot be undone.`)) return;
    await deleteWellside(product.id);
    setProducts((prev) => prev.filter((p) => p.id !== product.id));
  };

  const handleToggleStock = async (product) => {
    const nextInStock = !(product.inStock !== false);
    setProducts((prev) => prev.map((p) => (p.id === product.id ? { ...p, inStock: nextInStock } : p)));
    await updateWellside(product.id, { inStock: nextInStock });
  };

  const handleSaveBadge = async (product, badge) => {
    setProducts((prev) => prev.map((p) => (p.id === product.id ? { ...p, badge } : p)));
    await updateWellside(product.id, { badge });
  };

  if (loading) return <p className="text-white/60 font-mono text-sm">Loading…</p>;

  return (
    <div data-testid="admin-wellside-list-page">
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-display text-3xl text-white">Wellside Canopies</h1>
        <Link to="/admin/wellside-canopies/new" data-testid="admin-wellside-add-button" className="btn-primary">
          <Plus size={16} /> Add Product
        </Link>
      </div>
      <p className="text-white/50 text-sm font-mono mb-4">Drag the handle to reorder how products appear on the storefront. Toggle "In Stock" to control whether Buy Now is available. Badge sets the label shown over the main image (blank = none).</p>
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={products.map((p) => p.id)} strategy={verticalListSortingStrategy}>
          <div className="border-t border-[var(--border-default)]">
            {products.map((p) => (
              <SortableRow key={p.id} product={p} onDelete={handleDelete} onToggleStock={handleToggleStock} onSaveBadge={handleSaveBadge} />
            ))}
          </div>
        </SortableContext>
      </DndContext>
    </div>
  );
}
